import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, inject } from 'vue';

import type { CalendarDayParams } from './calendar-day-params';
import type { CalendarState } from './calendar-state';
import * as utils from './utils';

/**
 * `CalendarDay` component properties.
 */
export interface Props {
  index: number;
  dow: number;
  date: Date;
  itemCount: number;
}

/* eslint-disable @typescript-eslint/unified-signatures */

/**
 * `CalendarDay` component emits.
 */
export interface Emits {
  (event: 'click', eventData: MouseEvent): void;
  (event: 'dragstart', eventData: DragEvent): void;
  (event: 'drop', eventData: DragEvent): void;
  (event: 'dragover', eventData: DragEvent): void;
  (event: 'dragenter', eventData: DragEvent): void;
  (event: 'dragleave', eventData: DragEvent): void;
}

/* eslint-enable @typescript-eslint/unified-signatures */


const __default__ = { name: 'CalendarDay' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    index: null,
    dow: null,
    date: null,
    itemCount: null
  },
  emits: ["click", "dragstart", "drop", "dragover", "dragenter", "dragleave"],
  setup(__props: any, { emit }: { emit: ({
  (event: 'click', eventData: MouseEvent): void;
  (event: 'dragstart', eventData: DragEvent): void;
  (event: 'drop', eventData: DragEvent): void;
  (event: 'dragover', eventData: DragEvent): void;
  (event: 'dragenter', eventData: DragEvent): void;
  (event: 'dragleave', eventData: DragEvent): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  index: number;
  dow: number;
  date: Date;
  itemCount: number;
};





const calendar = reactive(inject('calendarState') as CalendarState);

const calendarDayParams = reactive(
  inject('calendarDayParams') as CalendarDayParams,
);

const classList = computed(() => {
  const isoYearMonthDay = utils.isoYearMonthDay(props.date);
  const isoMonthDay = utils.isoMonthDay(props.date);
  const paddedDay = utils.paddedDay(props.date);

  const dateClasses = calendar.dateClasses?.[isoYearMonthDay] ?? [];

  const items: string[] = [
    `dow${props.dow}`,
    `d${isoYearMonthDay}`,
    `d${isoMonthDay}`,
    `d${paddedDay}`,
    `instance${utils.instanceOfMonth(props.date)}`,
  ];

  if (utils.isSameDate(props.date, utils.today())) {
    items.push('today');
  }

  if (!utils.isSameMonth(props.date, calendar.defaultedShowDate)) {
    items.push('outside-of-month');
  }

  if (utils.isInPast(props.date)) {
    items.push('past');
  }

  if (utils.isInFuture(props.date)) {
    items.push('future');
  }

  if (utils.isLastDayOfMonth(props.date)) {
    items.push('last');
  }

  if (utils.isLastInstanceOfMonth(props.date)) {
    items.push('last-instance');
  }

  if (dayHasItems(props.date)) {
    items.push('has-items');
  }

  if (utils.isSameDate(props.date, calendar.selectionStart)) {
    items.push('selection-start');
  }

  if (utils.isSameDate(props.date, calendar.selectionEnd)) {
    items.push('selection-end');
  }

  if (props.index === 0) {
    items.push('week-start');
  }

  if (props.index === 6) {
    items.push('week-end');
  }

  if (Array.isArray(dateClasses)) {
    items.push(...dateClasses);
  } else {
    items.push(dateClasses);
  }

  return items;
});

const fomName = computed(() => {
  return calendar.showMonthNameOn1st && props.date.getDate() === 1
    ? calendar.monthNames[props.date.getMonth()]
    : '';
});

const dateLabel = computed(() => {
  return props.date.getDate().toString();
});

const dropEffect = computed(() => {
  let effect: 'move' | 'execute' | 'none';

  if (calendar.enableDragDrop && calendar.currentDragItem) {
    effect = 'move';
  } else if (calendar.enableDateSelection && calendar.dateSelectionOrigin) {
    effect = 'execute';
  } else {
    effect = 'none';
  }

  return effect;
});

const isSelected = computed(() => {
  if (!calendar.selectionStart || !calendar.selectionEnd) return false;

  if (props.date < utils.dateOnly(calendar.selectionStart)) return false;

  if (props.date > utils.dateOnly(calendar.selectionEnd)) return false;

  return true;
});

const isDraggable = computed(() => {
  return calendar.enableDateSelection;
});

const isGrabbed = computed(() => {
  return isDraggable.value ? isSelected.value : false;
});

const itemsOverflow = computed(() => {
  if (props.itemCount <= calendarDayParams.maxDisplayable) {
    return null;
  }

  const count = props.itemCount - calendarDayParams.maxDisplayable;
  const top =
    calendarDayParams.topOffset +
    (props.itemCount - count) * calendarDayParams.itemHeight;

  return { count, top };
});

//#region Helper Functions

function dayHasItems(day: Date) {
  return !!calendar.fixedItems.find(
    (d) => d.endDate >= day && utils.dateOnly(d.startDate) <= day,
  );
}

//#endregion Helper Functions

return { __sfc: true,props, emit, calendar, calendarDayParams, classList, fomName, dateLabel, dropEffect, isSelected, isDraggable, isGrabbed, itemsOverflow, dayHasItems }
}

})